<template>
  <div v-if="showGlobalControls" class="sp-interactive-accordion-group__controls">
    <sp-button variant="text" size="small" @click="expandAllCards">Expand All</sp-button>
    <sp-button variant="text" size="small" @click="collapseAllCards">Collapse All</sp-button>
  </div>

  <div ref="groupRoot" class="sp-interactive-accordion-group" :class="classModifiers">
    <slot />
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, useTemplateRef } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { getSlotContent } from "#ce/utils/slots";
import { toBoolean } from "#ce/utils/props";
import { props as propsDefinitions } from "./props/accordion-group.js";

const props = defineProps(propsDefinitions);

const { breakpointModifiers } = useBreakpoints();

const showGlobalControls = ref(toBoolean(props.showGlobalControls));
const groupRoot = useTemplateRef("groupRoot");
const cardElements = ref([]);

const hasCards = computed(() => cardElements.value.length > 0);

const classModifiers = computed(() => [...breakpointModifiers.value]);

watch(groupRoot, () => {
  cardElements.value = getSlotContent(groupRoot.value, { flatten: true }).filter(
    (node) => node.tagName === "SP-INTERACTIVE-CARD",
  );
});

watch(cardElements, async () => {
  await nextTick();
  updateChildrenContext();
});

function updateChildrenContext() {
  cardElements.value.forEach((cardEl) => {
    cardEl.setInteractionMode?.("expand");
  });
}

function expandAllCards() {
  if (!hasCards.value) {
    return;
  }

  cardElements.value.forEach((el) => el.expand?.());
}

function collapseAllCards() {
  if (!hasCards.value) {
    return;
  }

  cardElements.value.forEach((el) => el.collapse?.());
}
</script>

<style>
:host {
  --group-gap: var(--sp-comp-interactive-group-gap, var(--sp-ref-spacing-12));
  display: block;
}
</style>

<style lang="scss" scoped>
.sp-interactive-accordion-group__controls {
  display: flex;
  justify-content: flex-start;
  gap: var(--sp-ref-spacing-4);
  margin-bottom: var(--sp-ref-spacing-8);
}

.sp-interactive-accordion-group {
  display: flex;
  flex-direction: column;
  gap: var(--group-gap);
}
</style>
